// Header.js
import React, { useEffect, useState, useRef } from "react";
import { AppBar, Toolbar, Badge, Tooltip, IconButton } from "@mui/material";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import userIcon from "../../../../src/imgs/userIcon.svg";
import downArrow from "../../../../src/imgs/downArrow.svg";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { io } from "socket.io-client";
import { getAdminUnreadTotal } from "../../../api/services/communicationServices";
import { clearAdminFcmToken } from "../../../api/services/services";
import { getSocketUrl, getImageUrl } from "../../../api/config/apiConfig";
import NotificationDrawer from "../NotificationDrawer";
import ProfileModal from "../ProfileModal";
import {
  showDesktopBrowserNotification,
  requestDesktopNotificationPermission,
  registerServiceWorker,
} from "../../../api/utils/browserNotificationHelper";

// Web Audio Soft Chime Generator
const playChime = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(587.33, ctx.currentTime);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.12);

    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.48);
  } catch (_) {}
};

const Header = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [inAppBanner, setInAppBanner] = useState(null);
  const bannerTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentUser, setCurrentUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifPermission, setNotifPermission] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "default"
  );
  const [chatSocketState, setChatSocketState] = useState(null);
  const [rootSocketState, setRootSocketState] = useState(null);
  const socketRef = useRef(null);
  const rootSocketRef = useRef(null);
  const seenMessageIdsRef = useRef(new Set());

  const logout = async () => {
    try {
      await clearAdminFcmToken();
    } catch (_) {}
    localStorage.clear();
    window.location.pathname = "/login";
  };

  const triggerBanner = (bannerData) => {
    playChime();
    setInAppBanner(bannerData);
    if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
    bannerTimeoutRef.current = setTimeout(() => {
      setInAppBanner(null);
    }, 6000);
  };

  const requestNotificationPermission = async () => {
    const perm = await requestDesktopNotificationPermission();
    setNotifPermission(perm);
  };

  const fetchUnread = () => {
    getAdminUnreadTotal()
      .then((res) => {
        const val = res?.data?.data;
        const count = typeof val === "object" && val !== null ? (val?.total || 0) : (Number(val) || 0);
        setUnreadCount(count);
      })
      .catch(() => {});
  };

  useEffect(() => {
    // Register Service Worker with background navigation handler
    registerServiceWorker((data) => {
      navigate(data.url || "/communication", {
        state: {
          conversationId: data.conversationId,
          senderName: data.senderName,
          surveyId: data.surveyId,
        },
      });
    });

    const userData = localStorage.getItem("shelUserData");
    if (userData) {
      try {
        setCurrentUser(JSON.parse(userData));
      } catch (_) {}
    }

    const handleProfileUpdate = (e) => {
      if (e?.detail) {
        setCurrentUser(e.detail);
      } else {
        const freshData = localStorage.getItem("shelUserData");
        if (freshData) {
          try {
            setCurrentUser(JSON.parse(freshData));
          } catch (_) {}
        }
      }
    };
    window.addEventListener("profileUpdated", handleProfileUpdate);

    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);

    // 1. Setup Chat Socket for live incoming message alerts
    const chatSocketUrl = getSocketUrl("communication");
    const chatSocket = io(chatSocketUrl, {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 10,
      timeout: 5000,
    });
    socketRef.current = chatSocket;
    setChatSocketState(chatSocket);

    chatSocket.on("connect", () => {
      chatSocket.emit("joinAdmin");
    });
    chatSocket.emit("joinAdmin");

    const handleIncomingChatMessage = (raw) => {
      const msg = raw?.message || raw;
      if (!msg || !msg.uuid) return;

      // Deduplicate messages received across newMessage and messageBroadcast
      if (seenMessageIdsRef.current.has(msg.uuid)) return;
      seenMessageIdsRef.current.add(msg.uuid);
      setTimeout(() => {
        seenMessageIdsRef.current.delete(msg.uuid);
      }, 15000);

      // Check current user to avoid self notifications
      let currentAdminId = currentUser?.uuid || currentUser?.id;
      if (!currentAdminId) {
        try {
          const ud = JSON.parse(localStorage.getItem("shelUserData") || "{}");
          currentAdminId = ud?.uuid || ud?.id;
        } catch (_) {}
      }
      if (currentAdminId && msg.senderId === currentAdminId) {
        return;
      }

      fetchUnread();

      const senderName = msg.senderName || (msg.senderType === "ADMIN" ? "Admin Member" : "Surveyor");
      const title = `Message from ${senderName}`;
      const body =
        msg.messageType === "IMAGE"
          ? "Sent a photo 📷"
          : msg.messageType === "DOCUMENT"
          ? `Sent a document 📄 ${msg.fileName || ""}`
          : msg.content || "New message";

      const goToChat = () => {
        navigate("/communication", {
          state: {
            conversationId: msg.conversationId,
            senderName: msg.senderName,
            senderType: msg.senderType,
            surveyId: msg.surveyId,
          },
        });
      };

      const isOnCommPage = window.location.pathname === "/communication";
      const isTabHidden = document.hidden;

      // Only show in-app banner if not already inside Communication Hub
      if (!isOnCommPage) {
        triggerBanner({
          title,
          body,
          type: "CHAT",
          onClick: goToChat,
        });
      }

      // Always trigger Desktop / Background Browser Notification if tab is hidden OR not on communication page
      if (isTabHidden || !isOnCommPage) {
        showDesktopBrowserNotification({
          title,
          body,
          conversationId: msg.conversationId,
          senderName: msg.senderName,
          surveyId: msg.surveyId,
          url: "/communication",
        });
      }
    };

    chatSocket.on("newMessage", handleIncomingChatMessage);
    chatSocket.on("messageBroadcast", handleIncomingChatMessage);

    chatSocket.on("conversationUpdated", () => {
      fetchUnread();
    });

    // 2. Setup System Notification Socket
    const rootSocketUrl = getSocketUrl();
    const rootSocket = io(rootSocketUrl, {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 10,
      timeout: 5000,
    });
    rootSocketRef.current = rootSocket;
    setRootSocketState(rootSocket);

    rootSocket.on("connect", () => {
      rootSocket.emit("joinAdmin");
    });
    rootSocket.emit("joinAdmin");

    const handleIncomingNotif = (notif) => {
      fetchUnread();
      triggerBanner({
        title: notif.title || "New System Notification",
        body: notif.body || "A new update has been received",
        type: "NOTIF",
        onClick: () => {
          if (notif.surveyId) {
            navigate(`/dashboard/view-details/${notif.surveyId}`);
          }
        },
      });

      // Desktop / Background Browser Notification
      showDesktopBrowserNotification({
        title: notif.title || "Shelozon Notification",
        body: notif.body || "New update received",
        surveyId: notif.surveyId,
        url: notif.surveyId ? `/dashboard/view-details/${notif.surveyId}` : "/dashboard",
      });
    };

    rootSocket.on("notification:new", handleIncomingNotif);
    rootSocket.on("notification", handleIncomingNotif);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate);
      clearInterval(interval);
      if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
      chatSocket.disconnect();
      rootSocket.disconnect();
    };
  }, [navigate]);

  return (
    <>
      <AppBar
        position="sticky"
        className="cosmic-header no-print"
        elevation={0}
        style={{ height: "70px", zIndex: 1100 }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {/* Mobile Sidebar Toggle */}
          <div
            className="d-block d-md-none cursor-pointer"
            onClick={() => {
              const current = localStorage.getItem("isSideBarOpen") === "true";
              localStorage.setItem("isSideBarOpen", !current);
              window.dispatchEvent(new Event("sidebarChange"));
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 12H21"
                stroke="#1E293B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 6H21"
                stroke="#1E293B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 18H21"
                stroke="#1E293B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Spacer for Flex */}
          <div className="d-none d-md-block"></div>

          {/* Right Header Actions */}
          <div className="header-actions-group">
            {/* Desktop Notification Permission Pill / Indicator */}
            {notifPermission !== "granted" ? (
              <button
                className="header-notif-btn enable"
                onClick={requestNotificationPermission}
                title="Enable desktop push notifications"
              >
                <NotificationsNoneRoundedIcon style={{ fontSize: 18 }} />
                <span>Enable Alerts</span>
              </button>
            ) : (
              <Tooltip title="Desktop Push Notifications Active" arrow>
                <div className="header-notif-active-badge">
                  <NotificationsActiveRoundedIcon style={{ fontSize: 18, color: "#16a34a" }} />
                </div>
              </Tooltip>
            )}

            {/* Notification Center Drawer */}
            <NotificationDrawer socket={chatSocketState} rootSocket={rootSocketState} />

            {/* Quick Chat / Communication Hub Link */}
            <Tooltip title="Communication Hub" arrow>
              <IconButton
                className="header-chat-btn"
                onClick={() => navigate("/communication")}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <ForumRoundedIcon style={{ color: "#4f46e5", fontSize: 22 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Badge */}
            <div
              className="header-user-badge"
              onClick={() => setShowLogout(!showLogout)}
            >
              {currentUser?.avatar ? (
                <div style={{ position: "relative", width: "32px", height: "32px", borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
                  <img
                    src={getImageUrl(currentUser.avatar)}
                    className="header-user-avatar-img"
                    alt="User"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      if (e.currentTarget.nextSibling) {
                        e.currentTarget.nextSibling.style.display = "flex";
                      }
                    }}
                  />
                  <div className="header-user-avatar-initials" style={{ display: "none", position: "absolute", inset: 0 }}>
                    {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : "A"}
                  </div>
                </div>
              ) : (
                <div className="header-user-avatar-initials">
                  {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : "A"}
                </div>
              )}
              <div className="user-name">{currentUser?.name || "User"}</div>
              <img
                src={downArrow}
                height="16"
                width="16"
                alt="Toggle"
                style={{
                  transform: showLogout ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  opacity: 0.6,
                }}
              />
            </div>
          </div>
        </Toolbar>
      </AppBar>

      {/* User Actions Dropdown */}
      {showLogout && (
        <div className="logout-dropdown no-print">
          <div
            className="dropdown-menu-item"
            onClick={() => {
              setShowLogout(false);
              setProfileModalOpen(true);
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6366f1"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            My Profile & Personalisation
          </div>

          <div className="dropdown-divider"></div>

          <div
            className="dropdown-menu-item logout"
            onClick={logout}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Log Out
          </div>
        </div>
      )}

      {/* Admin Profile Modal */}
      <ProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        onProfileUpdated={(updated) => {
          setCurrentUser((prev) => ({ ...prev, ...updated }));
        }}
      />

      {/* Interactive Web In-App Floating Notification Banner */}
      {inAppBanner && (
        <div className="web-inapp-banner-container">
          <div
            className="web-inapp-banner"
            onClick={() => {
              inAppBanner.onClick?.();
              setInAppBanner(null);
            }}
          >
            <div
              className={`web-banner-icon ${
                inAppBanner.type === "CHAT" ? "chat" : "notif"
              }`}
            >
              {inAppBanner.type === "CHAT" ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
              )}
            </div>

            <div className="web-banner-content">
              <div className="web-banner-title">{inAppBanner.title}</div>
              <div className="web-banner-body">{inAppBanner.body}</div>
            </div>

            <button
              className="web-banner-close"
              onClick={(e) => {
                e.stopPropagation();
                setInAppBanner(null);
              }}
              aria-label="Close notification"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
